import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import CustomStyledTable from "../../../../../components/table/CustomStyledTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { Columns } from "./data";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import { TaxCodeDto } from "../../../../../models/tax-code.dto";
import {
	useDeleteTaxCode,
	useGetTaxCode,
	useGetTaxCodeSearch,
} from "../../../../../services/tax-code.service";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import TaxCodeModalForm from "./modal";
import { useGetCountry } from "../../../../../services/country.service";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { CountryOrderBy } from "../../../../../models/country.dto";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../../components/search/APISearchInput";
import { TaxCodesColumns } from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../../models/table-configuration.dto";
import useUserStore from "../../../../../state-management/useUserStore";

const TaxCodes = () => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditTaxCodeForm, setIsEditTaxCodeForm] = useState(false);
	const currentUser = useUserStore().currentUser.user;

	const { mutate: searchTaxCodes } = useGetTaxCodeSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<TaxCodeDto>({
		fetchHook: useGetTaxCode,
	});

	// Fetched data for select fields requiring data from
	const { data: countries } = useGetCountry({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: CountryOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditTaxCodeForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteTaxCode } = useDeleteTaxCode();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteTaxCode(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];
	const columnsWithAdditions = Columns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj,
	);

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Tax Codes</h2>
			<hr />
			<div className="flex justify-between items-center mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchTaxCodes}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Code",
										fieldName: "code",
									},
									{
										title: "Start Date",
										fieldName: "startDate",
									},
									{
										title: "End Date",
										fieldName: "endDate",
									},
									{
										title: "Effective Date",
										fieldName: "effectiveDate",
									},
									{
										title: "Expiry Date",
										fieldName: "expiryDate",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Country",
								fieldName: "countryId",
								fieldType: "SELECT_FIELD",
								options: countries?.data || [],
							},
							{
								title: "Enabled",
								fieldName: "enabled",
								fieldType: "YES_NO",
							},
							{
								title: "Default",
								fieldName: "isDefault",
								fieldType: "YES_NO",
							},
							{
								title: "Start Date",
								fieldName: "startDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "End Date",
								fieldName: "endDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "Effective Date",
								fieldName: "effectiveDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "Expiry Date",
								fieldName: "expiryDate",
								fieldType: "DATE_RANGE",
							},
						]}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New Tax Code
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.TAX_CODES}
				data={tableData}
				columns={TaxCodesColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditTaxCodeForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.code || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<TaxCodeModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditTaxCodeForm ? "Update Tax Code" : "Add New Tax Code"}
					isEditForm={isEditTaxCodeForm}
					tableAPIRef={tableAPIRef}
					countries={countries?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
		</div>
	);
};

export default TaxCodes;
